import _ from "lodash"

import { DealId } from "shared/hubrise/ids"
import * as Deal from "shared/hubrise/utils/Deal"
import { GenerateRefsOptions } from "shared/hubrise/utils/ref"

import { IDeal, IDealLine } from "../models"

import type { Change, OperationContext } from "./types"

export type DealOperations = {
  add(insertAfter: IDeal | undefined, values: Partial<IDeal>): IDeal
  delete(id: DealId): void
  move(deal: IDeal, newPosition: number): void
  reorderLines(id: DealId, lines: Array<IDealLine>): void
  update<T extends keyof IDeal>(id: DealId, fieldName: T, value: IDeal[T], options?: { collapse?: boolean }): void
  removeAllRefs(): void
  generateRefs(options: GenerateRefsOptions): void
  enable(dealId: DealId, enabled: boolean, variantRef?: string): void
  enableAll(enabled: boolean, variantRef?: string): void
}

export default ({ catalog, pushState }: OperationContext): DealOperations => ({
  add(insertAfter, values) {
    const { newCatalog, deal } = Deal.add(catalog, insertAfter, values)
    pushState(newCatalog, { model: "deal", operation: "add" })
    return deal
  },

  delete(id) {
    const newCatalog = Deal.doDelete(catalog, id)
    pushState(newCatalog, { model: "deal", operation: "delete", id })
  },

  move(deal, newPosition) {
    const newCatalog = Deal.move(catalog, deal, newPosition)
    const change: Change = { model: "deal", operation: "move", id: deal.id }
    pushState(newCatalog, change, (previousChange) => _.isEqual(previousChange, change))
  },

  reorderLines(id, lines) {
    const newCatalog = Deal.update(catalog, id, "lines", lines)
    const change: Change = {
      model: "deal",
      id,
      operation: "reorderLines",
    }
    pushState(newCatalog, change, (previousChange) => _.isEqual(previousChange, change))
  },

  update(id, fieldName, value, { collapse = true } = {}) {
    const newCatalog = Deal.update(catalog, id, fieldName, value)
    const change: Change = {
      model: "deal",
      id,
      operation: "update",
      fieldName,
    }
    pushState(newCatalog, change, (previousChange) => collapse && _.isEqual(previousChange, change))
  },

  removeAllRefs() {
    const newCatalog = Deal.removeAllRefs(catalog)
    pushState(newCatalog, { model: "deal", operation: "removeAllRefs" })
  },

  generateRefs(options) {
    const newCatalog = Deal.generateRefs(catalog, options)
    pushState(newCatalog, { model: "deal", operation: "generateRefs" })
  },

  enable(dealId, enabled, variantRef) {
    const newCatalog = Deal.enable(catalog, dealId, enabled, variantRef)
    const change: Change = {
      model: "deal",
      id: dealId,
      operation: "enable",
      variantRef,
    }
    pushState(newCatalog, change, (previousChange) => _.isEqual(previousChange, change))
  },

  enableAll(enabled, variantRef) {
    const newCatalog = Deal.enableAll(catalog, enabled, variantRef)
    const change: Change = { model: "deal", operation: "enableAll", variantRef }
    pushState(newCatalog, change)
  },
})
